/**
 * Shared text button. Before this, every screen hand-rolled its CTAs —
 * heights ranged h-9…h-14, radii full/2xl/xl/lg, and colors were picked
 * per call site. This unifies them into three semantic variants × three
 * sizes, all pill-shaped (`rounded-full`) with a consistent transition.
 *
 * Use this for text CTAs (Save, Send, Continue, Cancel). Icon-only
 * affordances still use `IconButton`. The chat composer's circular
 * send/share controls stay bespoke — they're icon buttons, not text.
 *
 * - variant: primary (oxblood fill) · secondary (oxblood outline) ·
 *   ghost (quiet, for Cancel / dismiss)
 * - size: sm (h-10) · md (h-11, default) · lg (h-12, hero CTAs)
 * - block: full-width
 * - busy: swap label for a spinner and disable
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  block?: boolean;
  busy?: boolean;
  children: ReactNode;
}

const SIZES: Record<Size, string> = {
  sm: 'h-10 px-4 text-sm',
  md: 'h-11 px-5 text-sm',
  lg: 'h-12 px-6 text-base',
};

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-oxblood text-paper hover:bg-oxblood/90 active:bg-oxblood/80',
  secondary:
    'border-2 border-oxblood text-oxblood hover:bg-oxblood/10 active:bg-oxblood/15',
  ghost: 'text-soft hover:text-ink hover:bg-line/40 active:bg-line/60',
};

export function Button({
  variant = 'primary',
  size = 'md',
  block = false,
  busy = false,
  disabled = false,
  type = 'button',
  className = '',
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={`inline-flex items-center justify-center gap-2 rounded-full font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none ${
        SIZES[size]
      } ${VARIANTS[variant]} ${block ? 'w-full' : ''} ${className}`}
      {...rest}
    >
      {busy ? (
        <span
          className="block w-5 h-5 rounded-full border-2 border-current border-t-transparent animate-spin"
          aria-hidden
        />
      ) : (
        children
      )}
    </button>
  );
}
